/**
 * UK short calendar date dd/mm/yy.
 * For plain YYYY-MM-DD strings, uses the date parts directly (no UTC shift).
 */
export function formatUkDdMmYy(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const datePart = value.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [y, m, d] = datePart.split('-');
      return `${d}/${m}/${y.slice(-2)}`;
    }
  }
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return typeof value === 'string' ? value : '';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`;
}

/** UK calendar date dd/mm/yyyy from YYYY-MM-DD (no UTC shift). */
export function formatUkDdMmYyyy(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const datePart = value.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [y, m, d] = datePart.split('-');
      return `${d}/${m}/${y}`;
    }
  }
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return typeof value === 'string' ? value : '';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

/**
 * Rewrite ISO dates inside leave-year period labels to UK dd/mm/yyyy.
 * e.g. "2026-01-01 → 2026-12-31" → "01/01/2026 → 31/12/2026"
 */
export function formatUkLeavePeriodLabel(label: string | null | undefined): string {
  if (!label) return '';
  return String(label).replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (_m, y, mo, d) => `${d}/${mo}/${y}`);
}

/** Stored leave-year start is MM-DD; display as UK DD-MM. */
export function leaveYearStartMmDdToUk(mmDd: string | null | undefined): string {
  const m = /^(\d{1,2})-(\d{1,2})$/.exec(String(mmDd ?? '').trim());
  if (!m) return String(mmDd ?? '').trim();
  return `${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}

/**
 * Parse UK DD-MM leave-year start into stored MM-DD.
 * Returns null if invalid.
 */
export function leaveYearStartUkToMmDd(ddMm: string | null | undefined): string | null {
  const m = /^(\d{1,2})-(\d{1,2})$/.exec(String(ddMm ?? '').trim());
  if (!m) return null;
  let day = Number(m[1]);
  let month = Number(m[2]);
  if (!Number.isFinite(month) || month < 1 || month > 12) return null;
  if (!Number.isFinite(day) || day < 1 || day > 31) return null;
  const probe = new Date(2024, month - 1, day);
  if (probe.getMonth() !== month - 1) day = new Date(2024, month, 0).getDate();
  return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
